import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Motion, spring } from '@allthings/react-motion';

const RESIZING = 'RESIZING'; // indicates the component is resizing
const WAITING = 'WAITING'; // indicates the component is waiting after a resize
const LOADED = 'LOADED'; // indicates the component is loaded

/**
 * updateHeights returns the from and to values when collapsing an elements
 *
 * @param {object} wrapper element
 * @param {object} content element
 * @param {bool} isOpened state of the collapse element
 */
const updateHeights = (wrapper, content, isOpened) => [
  wrapper.clientHeight,
  isOpened ? content.clientHeight : 0,
];

/**
 * Collapse creates a collapsible item and animates it using react-motion
 * for smooth springs
 */
const Collapse = memo((props) => {
  const { isOpened, children } = props;
  const [currentStatus, setStatus] = useState(LOADED);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);
  const contentRef = useRef(null);
  const wrapperRef = useRef(null);
  let raf;

  /**
   * onRest is an optional callback passed to Motion when the animation ends.
   * Here we set the status to waiting when its time to update the animation
   * for the next repaint.
   */
  const onRest = () => {
    raf = requestAnimationFrame(() => setStatus(WAITING));
    if (typeof props.onRest === 'function') {
      props.onRest();
    } else {
      console.error('onRest is not a function');
    }
  };

  /**
   * mimicking componentDidUpdate this effect runs every time props.isOpened is updated
   *
   * @return {func} the function to run on cleanup
   */
  useEffect(() => {
    // eslint-disable-next-line no-use-before-define
    const [start, end] = exportFunctions.updateHeights(
      wrapperRef.current,
      contentRef.current,
      isOpened
    );

    if (start !== end) {
      setStatus(RESIZING);
      setFrom(end);
      setTo(end);
    }

    // cancel the animation frame on cleanup.
    /* eslint consistent-return:off */
    return () => {
      cancelAnimationFrame(raf);
      setStatus(WAITING);
    };
  }, [isOpened]);

  /**
   * getWrapperStyle returns the styles for the wrapper
   *
   * @param {number} height
   * @return {object} the styles to apply to the wrapper
   */
  const getWrapperStyle = (height) => {
    // if the component is waiting set the height to auto for
    // responsive love
    if (currentStatus === WAITING && to) {
      return { height: 'auto' };
    }

    // while we're animating, set the height to the calculated height
    return { overflow: 'hidden', height: Math.max(0, height) };
  };

  /**
   * get the props to pass to Motion
   *
   * @return {object} the props to pass
   */
  const getMotionProps = () =>
    currentStatus === LOADED
      ? {
          defaultStyle: { height: to },
          style: { height: to },
        }
      : {
          defaultStyle: { height: from },
          style: {
            height: spring(to, {
              precision: 1,
            }),
          },
        };

  /**
   * renders the content that Motion will wrap
   * Motion requires a FaCC, hence the need for a separate method
   * no-shadow is disabled so that we don't have to maintain state
   * just for onRest
   */
  /* eslint no-shadow: off */
  const renderContent = ({ height, onRest, ...rest }) => {
    const wrapperStyle = getWrapperStyle(Math.max(0, height));

    return (
      <div
        ref={wrapperRef}
        style={wrapperStyle}
        className={isOpened ? 'isOpened collapsible' : 'collapsible'}
        {...rest}
      >
        <div ref={contentRef} className="collapsible-content">
          {children}
        </div>
      </div>
    );
  };

  // Pass the motion props and the onRest callback to Motion
  return (
    <Motion {...getMotionProps()} onRest={onRest}>
      {renderContent}
    </Motion>
  );
});

Collapse.displayName = 'Collapse';

Collapse.propTypes = {
  /** The children content to collapse */
  children: PropTypes.node.isRequired,
  /** Height of component, this gets automatically set */
  height: PropTypes.number,
  /** Whether the content is opened */
  isOpened: PropTypes.bool.isRequired,
  /** A callback function to run when the animation ends */
  onRest: PropTypes.func,
};

Collapse.defaultProps = {
  height: 0,
  onRest: () => null,
};

const exportFunctions = {
  Collapse,
  updateHeights,
};

export default exportFunctions;
